import type { UseFormReturn } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { z } from "zod";

const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ ]+$/u;
const companyNameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9 -]+$/u;

const subscriptionFormSchema = z
  .object({
    amount: z.number().optional(),

    // Contact/Business Information
    companyName: z
      .string({ required_error: "Company name is required" })
      .trim()
      .min(1, "Company name is required")
      .refine((v) => companyNameRegex.test(v), {
        message: "Only letters, numbers, spaces and dashes are allowed",
      }),

    currency: z.string().optional(),

    email: z
      .string({ required_error: "Email is required" })
      .trim()
      .email("This email is invalid, please try again."),
    // Contact Information
    fullName: z
      .string({ required_error: "Full name is required" })
      .trim()
      .min(1, "Full name is required")
      .refine((v) => nameRegex.test(v), {
        message: "Only letters and spaces are allowed",
      }),
    invoiceId: z.string().optional(),
    nameOnCard: z.string().optional(),
    paymentIntentClientSecret: z.string().optional(),

    paymentIntentId: z.string().optional(),
    // Payment Method
    paymentMethod: z.enum(["card", "bank", "google-pay", "apple-pay"], {
      required_error: "Please select a payment method",
    }),
    phoneNumber: z
      .string({ required_error: "Please enter a valid phone number" })
      .trim()
      .min(1, "Please enter a valid phone number"),
    priceId: z.string().optional(),
    // Backend/API derived fields (optional)
    productId: z.string().optional(),
    publishableKey: z.string().optional(),
    quantity: z.number().optional(),
    // Options
    saveDetails: z.boolean().default(false),
    stripePaymentMethodId: z.string().optional(),
    subscriptionId: z.string().optional(),
    termsAccepted: z.boolean().refine((accepted) => accepted, {
      message: "Please accept the terms to continue",
    }),
  })
  .superRefine((data, ctx) => {
    // If card payment is selected, name on card is required
    if (data.paymentMethod === "card" && !data.nameOnCard) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please enter the name on your card",
        path: ["nameOnCard"],
      });
    }

    // Phone validation using library helper for E.164 and country rules
    const phone = data.phoneNumber;

    if (!isValidPhoneNumber(phone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please enter a valid phone number",
        path: ["phoneNumber"],
      });
    }
  });

export type SubscriptionFormData = z.infer<typeof subscriptionFormSchema>;

export function useSubscriptionForm(
  defaultPaymentMethod: SubscriptionFormData["paymentMethod"],
): UseFormReturn<SubscriptionFormData> {
  return useForm<SubscriptionFormData>({
    defaultValues: {
      amount: undefined,
      companyName: "",
      currency: "",
      email: "",
      fullName: "",
      invoiceId: "",
      nameOnCard: "",
      paymentIntentClientSecret: "",
      paymentIntentId: "",
      paymentMethod: defaultPaymentMethod,
      phoneNumber: "",
      priceId: "",
      productId: "",
      publishableKey: "",
      quantity: undefined,
      saveDetails: false,
      stripePaymentMethodId: "",
      subscriptionId: "",
      termsAccepted: false,
    },
    mode: "onBlur",
    resolver: zodResolver(subscriptionFormSchema),
  });
}
